/**
 * User utility functions for managing user data and subscription status
 */

// Global user refresh callbacks
let globalUserRefreshCallbacks = [];

// Register a callback for global user refresh
export const registerUserRefreshCallback = (callback) => {
  globalUserRefreshCallbacks.push(callback);
  return () => {
    globalUserRefreshCallbacks = globalUserRefreshCallbacks.filter(cb => cb !== callback);
  };
};

// Trigger global user refresh
export const triggerGlobalUserRefresh = async (userId) => {
  try {
    console.log('🔄 Starting global user refresh for user:', userId);
    
    // Show a brief loading indicator
    const loadingEvent = new CustomEvent('userRefreshStarted', { detail: { userId } });
    window.dispatchEvent(loadingEvent);
    
    const updatedUser = await refreshUserData(userId);
    if (updatedUser) {
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Trigger all registered callbacks
      globalUserRefreshCallbacks.forEach(callback => {
        try {
          callback(updatedUser);
        } catch (error) {
          console.error('Error in user refresh callback:', error);
        }
      });
      
      // Trigger storage event for components that listen to it
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user',
        newValue: JSON.stringify(updatedUser)
      }));
      
      // Trigger custom event for immediate updates
      window.dispatchEvent(new CustomEvent('userDataUpdated', {
        detail: { user: updatedUser }
      }));
      
      // Notify refresh completion
      window.dispatchEvent(new CustomEvent('userRefreshCompleted', {
        detail: { user: updatedUser, userId }
      }));
      
      console.log('✅ Global user refresh completed:', updatedUser);
      return updatedUser;
    }
  } catch (error) {
    console.error('Error in global user refresh:', error);
    window.dispatchEvent(new CustomEvent('userRefreshError', {
      detail: { error, userId }
    }));
  }
  return null;
};

// Refresh user data from backend
export const refreshUserData = async (userId) => {
  try {
    const response = await fetch(`http://localhost:5000/api/user/${userId}`);
    if (response.ok) {
      const userData = await response.json();
      return userData;
    }
    return null;
  } catch (error) {
    console.error('Error refreshing user data:', error);
    return null;
  }
};

// Check if user has active subscription
export const hasActiveSubscription = (user) => {
  if (!user) return false;
  
  // Check if user is admin or premium role
  if (user.role >= 2) return true;
  
  // Check subscription status
  if (user.subscription && user.subscription.status === 1) return true;
  
  // Check trial status
  if (user.trial_end) {
    const trialEnd = new Date(user.trial_end);
    const now = new Date();
    return trialEnd > now;
  }
  
  return false;
};

// Get subscription status text
export const getSubscriptionStatus = (user) => {
  if (!user) return 'Guest';
  
  if (user.role >= 3) return 'Admin';
  if (user.role === 2) return 'Premium';
  
  if (user.subscription && user.subscription.status === 1) {
    return `Premium (${user.subscription.plan_type})`;
  }
  
  if (user.trial_end) {
    const trialEnd = new Date(user.trial_end);
    const now = new Date();
    if (trialEnd > now) {
      const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      return `Trial (${daysLeft} days left)`;
    } else {
      return 'Trial Expired';
    }
  }
  
  return 'Free';
};

// Update user data in localStorage and state
export const updateUserData = (userData, setUser) => {
  if (userData && userData.id && userData.id !== -1) {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('User data updated:', userData);
    return true;
  }
  return false;
};

// Force refresh user data from backend and update state
export const forceRefreshUser = async (userId, setUser) => {
  try {
    const freshUserData = await refreshUserData(userId);
    if (freshUserData) {
      updateUserData(freshUserData, setUser);
      return freshUserData;
    }
  } catch (error) {
    console.error('Error force refreshing user:', error);
  }
  return null;
};